import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../apiAdmin";
import { formatDuration } from "../utils/formatDuration";

interface PayoutDj {
  _id: string;
  name: string;
  email: string;
  avatarUrl?: string;
  slug?: string;
}

interface Payout {
  _id: string;
  batchId: string;
  createdAt: string;
  dj: PayoutDj;
  totalGross: number;
  totalNet: number;
  ordersCount: number;
  ordersAccepted: number;
  ordersDeclined: number;
  avgRequestValue: number;
  avgResponseMs?: number | null;
}

type SortField = "date" | "gross" | "avg";
type SortDir = "asc" | "desc";

function fmtAvatar(url?: string): string | null {
  if (!url) return null;
  return url.startsWith("http") ? url : `https://api.requesta.app${url}`;
}

export default function AdminAllPayoutsTab() {
  const navigate = useNavigate();
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState<SortField>("date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [dateSearch, setDateSearch] = useState("");

  useEffect(() => {
    adminApi
      .get("/payouts")
      .then((r) => setPayouts(r.data))
      .catch(() => setPayouts([]))
      .finally(() => setLoading(false));
  }, []);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const sortArrow = (field: SortField) => {
    if (sortField !== field) return <span className="sort-arrows muted-text">↕</span>;
    return <span className="sort-arrows">{sortDir === "desc" ? "↓" : "↑"}</span>;
  };

  const filtered = useMemo(() => {
    let list = [...payouts];

    // Date search filter
    if (dateSearch.trim()) {
      const q = dateSearch.trim().toLowerCase();
      list = list.filter((p) => {
        const d = new Date(p.createdAt);
        const dateStr = d.toLocaleDateString(undefined, { month: "numeric", day: "numeric", year: "2-digit" }).toLowerCase();
        return dateStr.includes(q);
      });
    }

    // Sort
    list.sort((a, b) => {
      let cmp = 0;
      if (sortField === "date") cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      else if (sortField === "gross") cmp = a.totalGross - b.totalGross;
      else if (sortField === "avg") cmp = a.avgRequestValue - b.avgRequestValue;
      return sortDir === "desc" ? -cmp : cmp;
    });

    return list;
  }, [payouts, sortField, sortDir, dateSearch]);

  if (loading) {
    return (
      <section className="fade-in">
        <p className="muted-text" style={{ textAlign: "center", padding: 40 }}>
          Loading payouts...
        </p>
      </section>
    );
  }

  return (
    <section className="fade-in">
      <div className="adm-card">
        <div className="card-header-flex" style={{ marginBottom: 12 }}>
          <h2 className="adm-section-title">All Payouts</h2>
          <input
            className="adm-search"
            placeholder="Search by date (e.g. 4/12/26)..."
            value={dateSearch}
            onChange={(e) => setDateSearch(e.target.value)}
            style={{ width: 300 }}
          />
        </div>

        {payouts.length === 0 ? (
          <p className="muted-text" style={{ textAlign: "center", padding: 30 }}>
            No payouts found.
          </p>
        ) : (
          <div className="adm-scrollable-full" style={{ maxHeight: "calc(100vh - 320px)" }}>
            <table className="adm-table-light adm-table-compact sticky-header">
              <thead>
                <tr>
                  <th
                    style={{ cursor: "pointer", userSelect: "none" }}
                    onClick={() => toggleSort("date")}
                  >
                    Date {sortArrow("date")}
                  </th>
                  <th>Time</th>
                  <th>Talent</th>
                  <th
                    style={{ cursor: "pointer", userSelect: "none" }}
                    onClick={() => toggleSort("gross")}
                  >
                    Gross {sortArrow("gross")}
                  </th>
                  <th>Net (Talent)</th>
                  <th># Orders</th>
                  <th
                    style={{ cursor: "pointer", userSelect: "none" }}
                    onClick={() => toggleSort("avg")}
                  >
                    Avg. Value {sortArrow("avg")}
                  </th>
                  <th>Avg. Response</th>
                  <th>Batch ID</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const dj = p.dj;
                  const avatarSrc = fmtAvatar(dj?.avatarUrl);
                  return (
                    <tr key={p._id} className="hover-row">
                      <td style={{ whiteSpace: "nowrap" }}>
                        {new Date(p.createdAt).toLocaleDateString(undefined, { month: "numeric", day: "numeric", year: "2-digit" })}
                      </td>
                      <td className="muted-text" style={{ whiteSpace: "nowrap" }}>
                        {new Date(p.createdAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td>
                        {dj ? (
                          <div
                            className="dj-cell dj-cell-compact clickable"
                            onClick={() => navigate(`/admin/dj/${dj._id}`)}
                          >
                            {avatarSrc ? (
                              <img src={avatarSrc} className="dj-cell-avatar-sm" alt="" />
                            ) : (
                              <div className="dj-cell-placeholder-sm">
                                {dj.name?.[0]?.toUpperCase() || "?"}
                              </div>
                            )}
                            <span className="dj-link-text" style={{ fontSize: "0.82rem" }}>
                              {dj.name}
                            </span>
                          </div>
                        ) : (
                          <span className="muted-text">Unknown</span>
                        )}
                      </td>
                      <td style={{ fontWeight: 600 }}>${p.totalGross.toFixed(2)}</td>
                      <td style={{ fontWeight: 700, color: "var(--adm-green)" }}>
                        ${p.totalNet.toFixed(2)}
                      </td>
                      <td>
                        <span style={{ fontWeight: 700 }}>{p.ordersCount}</span>{" "}
                        <span style={{ fontSize: "0.78rem" }}>
                          ({p.ordersAccepted}/{p.ordersDeclined})
                        </span>
                      </td>
                      <td>${p.avgRequestValue.toFixed(2)}</td>
                      <td className="muted-text" style={{ fontSize: "0.78rem" }}>
                        {p.avgResponseMs != null ? formatDuration(p.avgResponseMs) : "—"}
                      </td>
                      <td
                        className="muted-text"
                        style={{
                          fontSize: "0.72rem",
                          maxWidth: 140,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={p.batchId}
                      >
                        {p.batchId}
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={9} style={{ textAlign: "center", padding: 30, color: "#9ca3af" }}>
                      No payouts match your search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
